import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2 } from "lucide-react";
import api, { inr } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { Reveal } from "../components/Reveal";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || "";
  const { clearCart } = useShop();
  const [state, setState] = useState({ status: "loading" });
  const cleared = useRef(false);

  useEffect(() => {
    if (!orderId) { setState({ status: "failed" }); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/cashfree/status/${orderId}`);
        setState(data);
        if (data.status === "paid") {
          if (!cleared.current) { cleared.current = true; clearCart(); }
          return;
        }
        if (data.status === "failed" || data.status === "cancelled") return;
        attempts += 1;
        if (attempts < 40) timer = setTimeout(poll, 3000);
        else setState((s) => ({ ...s, status: "timeout" }));
      } catch {
        attempts += 1;
        if (attempts < 40) timer = setTimeout(poll, 3000);
        else setState((s) => ({ ...s, status: "timeout" }));
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = state.status;

  return (
    <main data-testid="payment-result-page" className="min-h-[70vh] flex flex-col items-center justify-center px-4 text-center py-20">
      <Reveal>
        {(status === "loading" || status === "pending") && (
          <>
            <Loader2 size={36} strokeWidth={1} className="text-vv-muted mx-auto mb-8 animate-spin" />
            <p className="vv-overline text-vv-muted mb-4">Confirming Payment</p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">ALMOST THERE.</h1>
            <p data-testid="payment-pending-note" className="mt-6 text-sm text-vv-muted max-w-md mx-auto">
              We're verifying your payment with the bank. This usually takes a few seconds — please don't close this page.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={40} strokeWidth={1} className="text-vv-forest mx-auto mb-8" />
            <p className="vv-overline text-vv-muted mb-4">Payment Successful</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">THANK YOU.</h1>
            <p data-testid="payment-success-ref" className="mt-6 text-sm text-vv-muted">
              Order <span className="font-mono text-vv-black">{state.ref}</span> confirmed — {inr(state.total)} paid.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link data-testid="payment-continue-shopping" to="/shop" className="vv-btn-dark">Continue Shopping</Link>
              <Link data-testid="payment-track-order" to="/account?tab=track" className="vv-btn-outline-dark">Track Order</Link>
            </div>
          </>
        )}
        {(status === "failed" || status === "cancelled" || status === "timeout") && (
          <>
            {status === "timeout"
              ? <Clock size={40} strokeWidth={1} className="text-vv-muted mx-auto mb-8" />
              : <XCircle size={40} strokeWidth={1} className="text-vv-burgundy mx-auto mb-8" />}
            <p className="vv-overline text-vv-muted mb-4">
              {status === "timeout" ? "Taking Longer Than Usual" : "Payment Not Completed"}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
              {status === "timeout" ? "CHECKING WITH THE BANK." : "NO CHARGE MADE."}
            </h1>
            <p data-testid="payment-failed-note" className="mt-6 text-sm text-vv-muted max-w-md mx-auto">
              {status === "timeout"
                ? "Your bank hasn't confirmed yet. If any amount was deducted it will be auto-refunded, and your reserved items are released back to stock."
                : "The payment didn't go through and you have not been charged. Your items have been released back to stock."}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link data-testid="payment-retry" to="/checkout" className="vv-btn-dark">Try Again</Link>
              <Link to="/shop" className="vv-btn-outline-dark">Continue Shopping</Link>
            </div>
          </>
        )}
      </Reveal>
    </main>
  );
}
